import React from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";

const sourceItems = [
  "Curated & verified businesses",
  "Diverse products & services",
  "Procurement-ready suppliers",
  "Direct business connections",
  "Purpose-driven sourcing",
];
const joinItems = [
  "Professional business profile",
  "Increase visibility & credibility",
  "Connect with buyers & partners",
  "Access opportunities & initiatives",
  "Be part of a trusted ecosystem",
];

const collage = [
  "https://images.unsplash.com/photo-1591086628264-52ca6bfc9f88?crop=entropy&cs=srgb&fm=jpg&q=85&w=500",
  "https://images.unsplash.com/photo-1610701596007-11502861dcfa?crop=entropy&cs=srgb&fm=jpg&q=85&w=500",
  "https://images.unsplash.com/photo-1604908176997-125f25cc6f3d?crop=entropy&cs=srgb&fm=jpg&q=85&w=500",
  "https://images.unsplash.com/photo-1607344645866-009c320b63e0?crop=entropy&cs=srgb&fm=jpg&q=85&w=500",
];

const joinWoman =
  "https://images.unsplash.com/photo-1573497620053-ea5300f94f21?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";

export default function SplitPanels({ onJoin, onExplore }) {
  return (
    <section id="buyers" className="py-24 md:py-32 bg-brand-paper2 relative" data-testid="split-panels-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-8 md:gap-12">
        {/* Looking to Source */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          className="bg-white rounded-[2rem] p-8 md:p-12 border border-brand-line relative overflow-hidden"
          data-testid="panel-source"
        >
          <span className="text-xs uppercase tracking-[0.25em] font-semibold text-brand-wine">
            For Buyers
          </span>
          <h3 className="mt-3 font-serif text-4xl md:text-5xl font-medium leading-tight text-brand-ink">
            Looking to <span className="italic text-brand-wine">source?</span>
          </h3>
          <p className="mt-4 text-brand-muted text-[15px] max-w-md leading-relaxed">
            Discover curated women-led businesses and professionals for products, services, procurement, and partnerships.
          </p>

          <div className="grid grid-cols-2 gap-3 mt-8">
            {collage.map((src, i) => (
              <div
                key={i}
                className={`overflow-hidden rounded-2xl ${
                  i % 2 === 0 ? "aspect-[4/5]" : "aspect-[4/3]"
                }`}
              >
                <img src={src} alt="Curated business" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              </div>
            ))}
          </div>

          <ul className="mt-8 space-y-3">
            {sourceItems.map((it) => (
              <li key={it} className="flex items-center gap-3 text-[15px] text-brand-ink">
                <span className="w-6 h-6 rounded-full bg-brand-wine text-white flex items-center justify-center">
                  <Check size={14} strokeWidth={2.5} />
                </span>
                {it}
              </li>
            ))}
          </ul>

          <button
            onClick={onExplore}
            className="mt-10 group inline-flex items-center gap-2 bg-brand-wine text-white rounded-lg pl-6 pr-5 py-3.5 font-medium text-[15px] transition-all duration-300 hover:bg-brand-wineDark hover:-translate-y-0.5"
            data-testid="panel-source-cta"
          >
            Explore the Business Exchange
            <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
          </button>
        </motion.div>

        {/* Join as a Business */}
        <motion.div
          id="businesses-panel"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          className="bg-brand-wine text-white rounded-[2rem] p-8 md:p-12 relative overflow-hidden"
          data-testid="panel-join"
        >
          <div className="absolute -top-8 -right-8 w-56 h-56 rounded-full bg-white/5" />
          <div className="absolute bottom-10 -right-10 w-40 h-40 rounded-full bg-white/5" />

          <span className="text-xs uppercase tracking-[0.25em] font-semibold text-white/70">
            For Businesses
          </span>
          <h3 className="mt-3 font-serif text-4xl md:text-5xl font-medium leading-tight">
            Join as a <span className="italic">business</span>.
          </h3>
          <p className="mt-4 text-white/80 text-[15px] max-w-md leading-relaxed">
            Showcase your business, get discovered, and connect with buyers, partners, and opportunities that help you grow.
          </p>

          <div className="mt-8 rounded-2xl overflow-hidden">
            <img
              src={joinWoman}
              alt="Woman business owner"
              className="w-full h-64 md:h-72 object-cover"
            />
          </div>

          <ul className="mt-8 space-y-3">
            {joinItems.map((it) => (
              <li key={it} className="flex items-center gap-3 text-[15px] text-white/95">
                <span className="w-6 h-6 rounded-full bg-white/15 border border-white/30 flex items-center justify-center">
                  <Check size={14} strokeWidth={2.5} />
                </span>
                {it}
              </li>
            ))}
          </ul>

          <button
            onClick={onJoin}
            className="mt-10 group inline-flex items-center gap-2 bg-white text-brand-wine rounded-lg pl-6 pr-5 py-3.5 font-medium text-[15px] transition-all duration-300 hover:bg-brand-cream hover:-translate-y-0.5"
            data-testid="panel-join-cta"
          >
            Join the Business Exchange
            <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
          </button>
        </motion.div>
      </div>
    </section>
  );
}
